import {
  Clock01Icon,
  CompassIcon,
  LockIcon,
  Tick02Icon
} from '@hugeicons/react'
import { DateTime } from 'luxon'
import clsx from 'clsx'
import { useAnalytics } from '@/shared/lib/integrations/analytics/useAnalytics'
import { useBlocker } from '@/shared/hooks/useBlocker'
import { useNavigation } from '@/shared/hooks/useNavigation'
import { Badge } from '@/shared/ui/Badge'
import { Button } from '@/shared/ui/Button'
import { Card } from '@/shared/ui/Card'
import { CardHint } from '@/shared/ui/CardHint'
import { ProgressBar } from '@/shared/ui/ProgressBar'
import { RewardBadge } from '@/shared/ui/RewardBadge'

import { JOURNEY_STATUS_LABELS } from '../constants/status'
import type { JourneyListItem } from '../hooks/useJourneysListView'
import { getBlockHintText } from '../lib/blockHintText'
import { formatOpensAtLabel } from '../lib/formatOpensAtLabel'

const progressVariantForStatus = (
  status: JourneyListItem['uiStatus']
): 'primary' | 'progress' | 'success' | 'locked' => {
  if (status === 'progress') return 'progress'
  if (status === 'success') return 'success'
  if (status === 'locked') return 'locked'
  return 'primary'
}

const buttonVariantForStatus = (
  status: JourneyListItem['uiStatus']
): 'primary' | 'progress' | 'success-ghost' | undefined => {
  if (status === 'available') return 'primary'
  if (status === 'progress') return 'progress'
  if (status === 'success') return 'success-ghost'
  return undefined
}

/* Data solta (sem ícone) sob o hint de bloqueio — legenda curta, não quebra;
   `text-left` pra acompanhar o hint de cima, já migrado pro `CardHint`. */
const dateCaption = 'block text-left text-2xs leading-snug text-gray-700'

type JourneyListCardProps = {
  journey: JourneyListItem
}

export const JourneyListCard = ({ journey }: JourneyListCardProps) => {
  const { showBlockAlert } = useBlocker()
  const { track, normalize } = useAnalytics()
  const { navigateTo } = useNavigation()

  const {
    id,
    uid,
    name,
    cover,
    uiStatus,
    blockResult,
    _progress,
    _totalCoinReward,
    _totalPointReward
  } = journey

  const { description } = journey

  const percent = Math.round(_progress.finishedPercent * 100)
  const blockHint =
    !blockResult.unlocked && blockResult.firstUnmet
      ? getBlockHintText(blockResult.firstUnmet)
      : null

  const timeBlock =
    blockResult.firstUnmet?.type === 'time' ? blockResult.firstUnmet : null
  const opensAtLabel = timeBlock ? formatOpensAtLabel(timeBlock.until) : null
  const opensAtDate = timeBlock
    ? DateTime.fromISO(timeBlock.until).toFormat('dd/MM/yyyy')
    : null

  const completedAtLabel =
    uiStatus === 'success' && journey._progress?.finished
      ? DateTime.now().toFormat('dd/MM')
      : null

  const navigate = () => {
    if (blockResult.unlocked) {
      navigateTo(`/jornadas/${uid}`)
      return
    }

    if (blockResult.firstUnmet) {
      showBlockAlert(blockResult.firstUnmet)
    }

    track.journey.blocked(normalize.journey({ id, uid, name }))
  }

  const ctaVariant = buttonVariantForStatus(uiStatus)
  const ctaLabel =
    uiStatus === 'available'
      ? 'Iniciar'
      : uiStatus === 'progress'
        ? 'Continuar'
        : uiStatus === 'success'
          ? 'Revisitar'
          : null

  const thumbLocked = uiStatus === 'locked'

  return (
    <Card
      className={clsx('shadow-sm', uiStatus === 'locked' && 'bg-gray-100')}
      onClick={navigate}
    >
      <Card.Body>
        <div className="flex items-stretch gap-2 sm:gap-3">
          {/* Thumbnail — aspect-square padrão (MASTER §7 L8), não `self-stretch` */}
          <div className="flex aspect-square w-20 shrink-0 self-start overflow-hidden rounded-[0.625rem]">
            {cover?.file?.url ? (
              <img
                src={cover.file.url}
                alt=""
                className={clsx(
                  'h-full w-full object-cover',
                  thumbLocked && 'opacity-65 grayscale'
                )}
              />
            ) : (
              <div
                className={clsx(
                  'flex min-h-0 w-full flex-1 items-center justify-center bg-brand-surface',
                  thumbLocked && 'opacity-65 grayscale'
                )}
              >
                {thumbLocked ? (
                  <LockIcon
                    size={24}
                    variant="duotone"
                    className="text-gray-400"
                  />
                ) : (
                  <CompassIcon
                    size={24}
                    variant="duotone"
                    className="text-primary opacity-45"
                  />
                )}
              </div>
            )}
          </div>

          {/* Main content */}
          <div className="flex min-w-0 flex-1 flex-col justify-center gap-1.5">
            {/* Dois eixos, dois badges: status (disponibilidade/progresso) e
                recência. O badge `new` só aparece dentro da janela de 1h — antes,
                a estrela de `nova` carimbava todo item liberado desde o começo
                do evento.

                `available` sem badge: é o status-padrão (maioria dos itens
                pode ser iniciada), badge nesse caso é ruído — tag existe pra
                sinalizar exceção (progresso, bloqueio, conclusão, encerramento). */}
            <div className="flex flex-wrap items-center gap-1.5">
              {uiStatus !== 'available' && (
                <Badge variant={uiStatus} size="sm" showIcon={false}>
                  {JOURNEY_STATUS_LABELS[uiStatus]}
                </Badge>
              )}
              {journey.isNew && (
                <Badge variant="new" size="sm">
                  Nova
                </Badge>
              )}
            </div>

            <h3
              className={clsx(
                'm-0 line-clamp-2 text-sm font-bold leading-snug text-gray-900',
                uiStatus === 'locked' && 'text-gray-500'
              )}
            >
              {name}
            </h3>

            {description && (
              <p className="m-0 line-clamp-2 text-2xs leading-snug text-gray-700">
                {description}
              </p>
            )}

            {(_totalPointReward > 0 || _totalCoinReward > 0) && (
              <RewardBadge.Group>
                {_totalPointReward > 0 && (
                  <RewardBadge
                    type="xp"
                    value={_totalPointReward}
                    variant="inline"
                  />
                )}
                {_totalCoinReward > 0 && (
                  <RewardBadge
                    type="coins"
                    value={_totalCoinReward}
                    variant="inline"
                  />
                )}
              </RewardBadge.Group>
            )}

            <div className="mt-0.5 flex items-center gap-2">
              <ProgressBar
                className="min-w-0 flex-1"
                value={uiStatus === 'locked' ? 0 : percent}
                variant={progressVariantForStatus(uiStatus)}
                size="sm"
              />
              {uiStatus !== 'locked' && (
                <span className="shrink-0 text-2xs font-bold leading-none text-gray-500">
                  {percent}%
                </span>
              )}
            </div>
          </div>

          {/* Rail — largura via token (MASTER §7 L8), não número solto */}
          <div className="flex min-h-20 w-[var(--rail-w-sm)] shrink-0 flex-col">
            <div className="flex flex-1 flex-col items-end justify-center gap-0.5 text-right">
              {opensAtLabel && (
                <CardHint
                  icon={<Clock01Icon size={14} aria-hidden />}
                  className="font-semibold text-danger"
                >
                  {opensAtLabel}
                </CardHint>
              )}
              {opensAtDate && (
                <span className={dateCaption}>{opensAtDate}</span>
              )}

              {blockHint && !opensAtLabel && (
                <CardHint icon={<LockIcon size={14} aria-hidden />}>
                  {blockHint}
                </CardHint>
              )}

              {uiStatus === 'success' && !blockHint && (
                <CardHint
                  icon={<Tick02Icon size={14} aria-hidden />}
                  className="font-semibold text-status-success"
                >
                  {completedAtLabel
                    ? `Concluída em ${completedAtLabel}`
                    : 'Concluída'}
                </CardHint>
              )}

              {uiStatus === 'progress' && !opensAtLabel && !blockHint && (
                <CardHint
                  icon={<Clock01Icon size={14} aria-hidden />}
                  className="font-semibold text-status-progress"
                >
                  Em andamento
                </CardHint>
              )}
            </div>

            <div className="flex flex-col items-stretch">
              {ctaVariant && ctaLabel && (
                <Button
                  variant={ctaVariant}
                  size="sm"
                  className="w-full justify-center font-bold"
                  onClick={e => {
                    e.stopPropagation()
                    navigate()
                  }}
                >
                  {ctaLabel}
                </Button>
              )}
            </div>
          </div>
        </div>
      </Card.Body>
    </Card>
  )
}
